import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { keyframes } from 'styled-components'
import CanvasBackground from '../../components/Background/CanvasBackground'
import BackofficeHeader from '../../components/AuthHeader/BackofficeHeader'
import ModernLoginForm from './Forms/ModernLoginForm'
import VerifySection from './VerifySection'

const fadeInUp = keyframes`
  0% {
    opacity: 0
    transform: translateY(30px)
  }
  100% {
    opacity: 1
    transform: translateY(0)
  }
`

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(100, 149, 237, 0.1),
                0 0 40px rgba(100, 149, 237, 0.05),
                0 0 80px rgba(100, 149, 237, 0.02)
  }
  50% {
    box-shadow: 0 0 30px rgba(100, 149, 237, 0.2),
                0 0 60px rgba(100, 149, 237, 0.1),
                0 0 120px rgba(100, 149, 237, 0.05)
  }
`

const Container = styled.div`
  height: 100vh
  width: 100%
  position: fixed
  top: 0
  left: 0
  display: flex
  flex-direction: column
  align-items: center
  justify-content: flex-start
  padding: 60px 20px 20px 20px
  overflow: hidden
  box-sizing: border-box
  z-index: 100
  background: transparent
`

const LoginCard = styled.div`
  background: rgba(15, 15, 35, 0.95) !important
  backdrop-filter: blur(20px)
  border: 1px solid rgba(100, 149, 237, 0.2) !important
  border-radius: 20px !important
  padding: 50px !important
  width: 100% !important
  max-width: 520px !important
  animation: ${fadeInUp} 0.8s ease-out, ${pulseGlow} 4s ease-in-out infinite
  position: relative !important
  margin-top: 20px !important
  z-index: 1000 !important
  display: block !important

  &::before {
    content: ''
    position: absolute
    top: 0
    left: 0
    right: 0
    bottom: 0
    background: linear-gradient(
      135deg,
      rgba(100, 149, 237, 0.1) 0%,
      rgba(30, 144, 255, 0.05) 50%,
      rgba(65, 105, 225, 0.1) 100%
    )
    border-radius: 20px
    z-index: -1
  }

  @media (max-width: 1200px) {
    padding: 45px
    margin-top: 15px
    max-width: 480px
  }

  @media (max-width: 768px) {
    padding: 35px
    margin-top: 10px
    max-width: 420px
  }

  @media (max-width: 480px) {
    padding: 30px
    margin-top: 10px
    max-width: 360px
    border-radius: 15px
  }
`

const LoginTitle = styled.h2`
  color: #ffffff
  font-size: 2.8rem
  font-weight: 600
  text-align: center
  margin: 0 0 30px 0
  letter-spacing: 1px

  @media (max-width: 1200px) {
    font-size: 2.4rem
  }

  @media (max-width: 768px) {
    font-size: 2rem
  }

  @media (max-width: 480px) {
    font-size: 1.8rem
    margin: 0 0 25px 0
  }
`

const WelcomeText = styled.p`
  color: rgba(255, 255, 255, 0.7)
  text-align: center
  margin: 0 0 30px 0
  font-size: 1.5rem
  line-height: 1.5

  @media (max-width: 1200px) {
    font-size: 1.3rem
  }

  @media (max-width: 768px) {
    font-size: 1.1rem
  }

  @media (max-width: 480px) {
    font-size: 1rem
    margin: 0 0 25px 0
  }
`

const FormContainer = styled.div`
  width: 100%
`

const FooterText = styled.div`
  text-align: center
  margin-top: 30px
  color: rgba(255, 255, 255, 0.5)
  font-size: 0.85rem

  @media (max-width: 480px) {
    margin-top: 25px
    font-size: 0.8rem
  }
`

const SecurityBadge = styled.div`
  display: flex
  align-items: center
  justify-content: center
  gap: 8px
  margin-top: 20px
  color: rgba(100, 149, 237, 0.8)
  font-size: 0.8rem

  &::before {
    content: '🔒'
    font-size: 0.9rem
  }
`

interface ModernLoginSectionProps {
  className?: string
}

const ModernLoginSection: React.FC<ModernLoginSectionProps> = ({ className }) => {
  const [loginSuccess, setLoginSuccess] = useState('')
  const { t } = useTranslation()

  return (
    <>
      <CanvasBackground />
      <Container
        className={className}
        style={{
          height: '100vh',
          width: '100%',
          position: 'fixed',
          top: 0,
          left: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '60px 20px 20px 20px',
          overflow: 'hidden',
          boxSizing: 'border-box',
          zIndex: 100,
          backgroundColor: 'transparent'
        }}
      >
        <BackofficeHeader subtitle="ADMIN CONTROL PANEL" />

        {loginSuccess?.length > 5 ? (
          <VerifySection
            authSuccess={loginSuccess}
            setAuthSuccess={setLoginSuccess}
          />
        ) : (
          <LoginCard
          style={{
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(52, 58, 64, 0.2)',
            borderRadius: '15px',
            padding: '35px',
            width: '100%',
            maxWidth: '450px',
            position: 'relative',
            marginTop: '10px',
            zIndex: 1000,
            display: 'block',
            boxShadow: '0 16px 48px rgba(52, 58, 64, 0.2), 0 8px 16px rgba(52, 58, 64, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
          }}
        >
          <LoginTitle style={{ color: '#212529', fontSize: '2.2rem', textAlign: 'center', margin: '0 0 20px 0' }}>
            {t('login')}
          </LoginTitle>
          <WelcomeText style={{ color: 'rgba(52, 58, 64, 0.8)', textAlign: 'center', fontSize: '1.1rem', margin: '0 0 25px 0' }}>
            관리자 계정으로 로그인하여 백오피스에 접속하세요
          </WelcomeText>

          <FormContainer>
            <ModernLoginForm setLoginSuccess={setLoginSuccess} />
          </FormContainer>

          <SecurityBadge style={{ color: 'rgba(73, 80, 87, 0.8)', textAlign: 'center', marginTop: '15px', fontSize: '0.9rem' }}>
            🔒 보안 연결로 보호됨
          </SecurityBadge>

          <FooterText style={{ color: 'rgba(108, 117, 125, 0.6)', textAlign: 'center', marginTop: '15px', fontSize: '0.8rem' }}>
            © {new Date().getFullYear()} BACKOFFICE. All rights reserved.
          </FooterText>
          </LoginCard>
        )}
      </Container>
    </>
  )
}

export default React.memo(ModernLoginSection)